/**
 * Act III — Out of Egypt (Exodus – Deuteronomy).
 *
 * The promise made to one household in Act II is now a people, and the people
 * are slaves. Four chapters: the name given at the bush, the night and the sea,
 * the mountain and the calf, and forty years of walking that end one river
 * short of the land.
 *
 * Chapter ids and verseIds are stable — progress is persisted against them, so
 * they must not be renamed.
 *
 * Scripture quoted inside scenes is the World English Bible (British Edition),
 * public domain, wrapped in *asterisks* for the serif face.
 */

export const ACT_RESCUE = {
  id: 'rescue',
  title: 'Out of Egypt',
  summary: 'A nation is born out of slavery, given a name for God and a way to live.',
  span: 'Exodus – Deuteronomy',
  chapters: [
    // ── 1 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-bush',
      title: 'The Name',
      where: 'Exodus 1–4',
      era: 'c. 1450 BC',
      artCaption: 'A thornbush alight in Midian, and a pair of sandals left on the ground',
      scenes: [
        "Four hundred years pass in a sentence. Joseph's family arrived in Egypt as seventy people and honoured guests. Exodus opens with them numerous enough to frighten a government. A new pharaoh, who never knew Joseph, puts them to forced labour. The numbers keep rising, so he escalates. First a quiet instruction to the midwives. Then an open decree: every newborn Hebrew boy is to be thrown into the Nile. Exodus 2 ends with the only thing slaves can still do. They groan. God hears, and *God remembered his covenant with Abraham*.",

        "The first people in the Bible to defy a king are two midwives. Shiphrah and Puah are ordered to kill the boys as they are delivered. They simply do not. Summoned to explain, they tell the most powerful man in the region a very convenient story: Hebrew women give birth too fast for us to get there. Exodus writes down both their names. It never writes down his. The pharaoh is a job title for the whole book. Two women at the bottom of the workforce are named and thanked.",

        "Moses' mother hides him for three months. Then she puts him in a waterproofed basket among the reeds. The Bible uses the same unusual word for that basket that it used for Noah's boat. A sealed box, carrying a future through water that should have ended it — for the second time. Pharaoh's daughter finds him, lifts him out and keeps him. She names him Moses, because, she says, she drew him out of the water. He grows up in the palace of the government that ordered his death.",

        "At forty he kills an Egyptian overseer. He checks first that nobody is watching, and finds out the next morning that somebody was. He runs east to Midian, marries a priest's daughter, and minds another man's sheep for the next forty years. Then a bush burns and is not burnt up. It is not a great tree. It is a thornbush, desert scrub, the least impressive plant on offer. Moses turns aside to look, and is told to take his sandals off. This is the first ground in the Bible called holy.",

        "Then he is given a job, and spends the next chapter and a half trying to hand it back. Who am I? Who shall I say sent me? They will not believe me. I have never been any good at speaking. And at last, with nothing left: please send somebody else. God answers the first four objections. The fifth makes him angry. He still does not withdraw the call — he sends Aaron along to do the talking. Moses is eighty. Prince, fugitive, shepherd. None of it looks like preparation.",

        "The second objection gets the strangest answer in the Bible. *I AM WHO I AM.* That is simply the verb to be, and it can as fairly be read I will be what I will be — a promise rather than a definition. Out of it comes God's own four-letter name. Later Jews stopped saying it aloud and read Lord in its place. That is why English Bibles still print LORD in small capitals: it marks the spot where the name itself stands. A God who simply is, and who is about to move.",
      ],
      verseId: 'exo-3-14',
      takeaway: 'God rescues because he remembers his promises, and he gives his own name to do it.',
      questions: [
        {
          id: 'sq-bush-1',
          question: 'What name does God give Moses at the burning bush?',
          options: ['The Almighty', 'The Rock of Israel', 'I AM WHO I AM', 'The God of Sinai'],
          answer: 2,
          explanation:
            'Exodus 3:14. From this comes the name rendered LORD in small capitals throughout the Old Testament — and the reason the “I am” sayings in John’s Gospel got Jesus into so much trouble.',
        },
        {
          id: 'sq-bush-2',
          question: 'Who are the first people in Exodus to disobey the king’s order?',
          options: [
            'Moses and Aaron',
            'Two Hebrew midwives, Shiphrah and Puah',
            'Pharaoh’s daughter',
            'Jethro, the priest of Midian',
          ],
          answer: 1,
          explanation:
            'Exodus 1:15–21. The narrator preserves the names of two working women and never gives the pharaoh one, which is a judgement about importance made purely by what is written down.',
        },
        {
          id: 'sq-bush-3',
          question: 'What does Exodus say moved God to act on Israel’s behalf?',
          options: [
            'Moses persuaded him at the bush',
            'Their term of servitude had legally expired',
            'They had built him an altar in Goshen',
            'He remembered his covenant with Abraham, Isaac and Jacob',
          ],
          answer: 3,
          explanation:
            'Exodus 2:24. The rescue is presented as an old promise coming due rather than a fresh decision — the same covenant made to a childless man in Genesis 15.',
        },
        {
          id: 'sq-bush-4',
          question: 'Why do English Bibles print LORD in small capitals?',
          options: [
            'To mark the places where the divine name given at the bush stands in the Hebrew',
            'To distinguish Old Testament passages from New Testament ones',
            'To show that the word was sung rather than spoken',
            'To flag words the translators supplied for clarity',
          ],
          answer: 0,
          explanation:
            'The four-letter name went unpronounced in later Jewish practice, with “Lord” read aloud in its place. The typographical convention preserves the substitution rather than hiding it.',
        },
      ],
    },

    // ── 2 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-sea',
      title: 'The Sea',
      where: 'Exodus 5–15',
      era: 'c. 1450 BC',
      artCaption: "The sea standing in two walls, and a road opened through it",
      scenes: [
        "Moses and Aaron walk into court with one line: *Let my people go, that they may hold a feast to me in the wilderness.* The answer is contempt. Who is the LORD, that I should listen to him? Then Pharaoh raises the quota. The workers must now find their own straw and still make the same number of bricks. The Israelite foremen are beaten for the shortfall. They come out, find Moses waiting, and turn on him. The first instalment of rescue makes everything measurably worse.",

        "The ten plagues are not random misfortunes. Each one lands on something Egypt trusted: the Nile that fed the country, the livestock, the crops, the sun, and finally the heir of a king who was himself counted a god. Exodus says it outright — this is a judgement on the gods of Egypt. One question the book never closes. Sometimes Pharaoh hardens his own heart. Sometimes God hardens it. Readers have argued for centuries over whether the man is being sentenced, or simply becoming at speed what he already was.",

        "The last night is survived only by households who mark their doorposts with the blood of a lamb. The meal is eaten dressed for travel — belt on, sandals on, staff in hand — and with flat bread, because there is no time to let dough rise. Then a detail people skip. God resets the calendar. The month of the rescue becomes the first month of the year. This people will count time from the night they were let out. It is called Passover, and Jesus will choose it as the night to be arrested.",

        "They leave after four hundred and thirty years. Exodus gives a figure: six hundred thousand men on foot, besides children. That would put well over a million people on the road, and the number is genuinely disputed. The Hebrew word for a thousand can also mean a clan or a fighting unit, and the desert would not have fed a million. What is not disputed is who went. A mixed crowd, Exodus says — other people, not descended from Jacob, walking out under the same cover. Moses carries Joseph's bones.",

        "Days later they are pinned against the water with Egyptian chariots closing in behind. The mood turns instantly. Were there no graves in Egypt, they ask Moses, that you brought us out here to die? His reply is one line, and it is the whole logic of the book. *The LORD will fight for you, and you shall be still.* An east wind blows all night. The sea opens. They walk through on dry ground, and the army that follows them in does not come out.",

        "On the far bank they sing, and Miriam takes up a tambourine and leads the women dancing. The song is one of the oldest things in the Bible. This is Israel's founding memory, and the striking thing is what is missing from it. No battle won. No ransom paid. No clever escape. They were carried out, and contributed nothing to it. Three days later, at a spring too bitter to drink, the same people are complaining again. The rescue is finished and the rescued are not, which is what the next forty years are about.",
      ],
      verseId: 'exo-14-14',
      takeaway: 'Israel’s founding memory is a rescue they contributed nothing to.',
      questions: [
        {
          id: 'sq-sea-1',
          question: 'What were the Israelites told to put on their doorposts on the night of the Passover?',
          options: [
            'Branches of hyssop',
            'The blood of a lamb',
            'A written blessing',
            'Ashes from the altar',
          ],
          answer: 1,
          explanation:
            'Exodus 12. John the Baptist’s greeting — “Behold, the Lamb of God” — lands on ears that had kept this feast every year of their lives.',
        },
        {
          id: 'sq-sea-2',
          question: 'What else changed for Israel on the night of the Passover?',
          options: [
            'The sabbath moved to the first day of the week',
            'Sacrifice was restricted to the priests',
            'The month of the rescue became the first month of their year',
            'They were forbidden to eat meat',
          ],
          answer: 2,
          explanation:
            'Exodus 12:2. Most ancient calendars were anchored to a harvest or a king’s accession. Israel’s was reset to the night they walked out of slavery.',
        },
        {
          id: 'sq-sea-3',
          question: 'Why does the sea crossing matter so much for how Israel understood itself?',
          options: [
            'It proved they could defeat an empire in open battle',
            'It showed Moses to be a capable general',
            'It won them a treaty with Egypt',
            'They were rescued without contributing anything to it',
          ],
          answer: 3,
          explanation:
            'The instruction at the sea is to stand still. Every later appeal to this event — in the psalms, the prophets and the New Testament — treats being rescued, not being strong, as the thing that defines them.',
        },
      ],
    },

    // ── 3 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-sinai',
      title: 'The Mountain Smokes',
      where: 'Exodus 19–20, 32–34',
      era: 'c. 1450 BC',
      artCaption: 'A mountain wrapped in smoke, and two tablets carried down it',
      scenes: [
        "Three months out they camp at the foot of Sinai, and the mountain begins to smoke. There is fire on it, and cloud, and a trumpet blast that grows louder instead of fading. A line is marked round the base, because nobody is to touch it. They will stay here for nearly a year. The proportions are worth noticing. Arriving at the mountain and leaving it are about sixty chapters apart — three times the space given to the whole escape from Egypt. The story slows almost to a stop.",

        "What is given at the mountain is not a list of rules first. It is a relationship, and a job. *You have seen what I did to the Egyptians, and how I bore you on eagles' wings and brought you to myself.* Then the job. They are to be a kingdom of priests: a whole nation doing for the world what a priest does for a nation, standing in between. Only then does the law arrive, and it opens with a reminder rather than a command. I brought you out of Egypt.",

        "The first word is the hinge for all the rest. *You shall have no other gods before me.* Everything else hangs off that one loyalty — the sabbath, honouring parents, the bans on murder, theft and envy. Hebrew does not call them commandments. It calls them the ten words. And the traditions cut them up differently. Jews count the reminder about Egypt as the first. Catholics and Lutherans fold images into the first and split coveting in two. Orthodox and Reformed churches keep images separate. Everyone gets ten. Nobody gets quite the same ten.",

        "The shape of it was familiar at the time, because it is the shape of a treaty. Agreements between a great king and a smaller one ran in a fixed order. The king names himself. He recounts what he has already done for the other party. He sets out the terms. The text is stored in a temple and read aloud at intervals. Exodus follows that order closely. It may also explain the two tablets: treaties were copied twice, one for each side — and here both sides kept theirs in the same place.",

        "Then, while Moses is still up there, the people at the bottom melt down their gold jewellery and make a calf. What they say about it is easy to miss. Aaron announces a feast to the LORD — the same name from the bush — and the crowd says the calf is the god who brought them up out of Egypt. This is not swapping one god for another. It is getting the right God into a shape you can see and manage, which is exactly what the second word forbids. Moses breaks the tablets.",

        "What happens next is why the story survives. God offers to wipe them out and start again with Moses. Moses refuses. He argues back, appeals to the promise sworn to Abraham, and offers to be blotted out himself instead. The covenant is remade. On the second climb God describes his own character in the sentence Israel will quote back more than any other: merciful and gracious, *slow to anger*, overflowing with faithful love. Exodus then spends thirteen chapters on a tent, because the point of the rescue was that he meant to move in.",
      ],
      verseId: 'exo-20-3',
      takeaway: 'The law came after the rescue, as a way of living with the God who did it.',
      questions: [
        {
          id: 'sq-sinai-1',
          question: 'What did the Israelites make while Moses was receiving the law on Sinai?',
          options: [
            'An ark of acacia wood',
            'A bronze serpent',
            'A tower of stones',
            'A golden calf',
          ],
          answer: 3,
          explanation:
            'Exodus 32. The covenant is broken before Moses is back down the mountain — and it is renewed anyway, which is the first great demonstration of what the Old Testament means by mercy.',
        },
        {
          id: 'sq-sinai-2',
          question: 'What comes immediately before the Ten Commandments in Exodus 20?',
          options: [
            'A reminder that God had already brought them out of Egypt',
            'A warning about the nations living in Canaan',
            'A census of the twelve tribes',
            'The plans for the tabernacle',
          ],
          answer: 0,
          explanation:
            'The law opens with what God has done, not with what Israel must do. The commandments are the shape of life with a rescuer, not the terms on which the rescue was granted.',
        },
        {
          id: 'sq-sinai-3',
          question: 'What did the people at the foot of the mountain say the golden calf was?',
          options: [
            'A gift for the Egyptians who had followed them',
            'A throne for Moses, who had not come back',
            'The god who had brought them up out of Egypt',
            'A likeness of the Egyptian bull god Apis',
          ],
          answer: 2,
          explanation:
            'Exodus 32:4–5, where Aaron then proclaims a feast to the LORD. Their sin was not picking a different god but building a manageable image of the right one — which is why the second word exists at all.',
        },
      ],
    },

    // ── 4 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-wilderness',
      title: 'Forty Years',
      where: 'Numbers – Deuteronomy',
      era: 'c. 1450–1410 BC',
      artCaption: "The land promised, seen once from Mount Nebo and never entered",
      scenes: [
        "Deuteronomy opens with a fact that works like a wound. It is eleven days' journey from the mountain to the edge of the promised land. It takes them forty years. At the border they send twelve scouts into the country for forty days. The scouts come back with a single bunch of grapes so heavy it is slung on a pole between two men — and ten of them say it cannot be done. The cities are walled, the people enormous. Caleb and Joshua argue. The nation believes the ten and refuses to go in.",

        "The sentence handed down has a grim arithmetic to it. A year in the desert for every day the scouts spent in the land. Forty days, forty years. The book of Numbers is built around it. It opens with a count of the fighting men. Near the end it counts their sons. Between the two lists, everyone named in the first has died in the desert, except Caleb and Joshua. That is where the English name comes from. The Hebrew name is simply: in the wilderness.",

        "Numbers is an uncomfortable book, and the discomfort is how ordinary the failure was. The complaints are mostly about food. God feeds them every day with something they cannot identify. The name manna comes from their own baffled question: what is it? Within a year they are sick of it and remembering Egypt fondly. The list they recite is oddly specific and completely convincing. The fish, the cucumbers, the melons, the leeks, the onions, the garlic. A generation walks away from the promise over vegetables.",

        "Two episodes here have long afterlives. When the camp is dying of snakebite, Moses is told to mount a bronze snake on a pole, and anyone who looks at it lives. Jesus later points at that object to explain his own execution. And a foreign prophet called Balaam is hired to curse Israel and can only bless it. In 1967 archaeologists at Deir Alla in Jordan found writing on a plaster wall. It is older than most surviving Hebrew scripture, and opens with the words of Balaam son of Beor, a seer of the gods.",

        "Deuteronomy is Moses at a hundred and twenty, preaching to the children of the generation that refused. He stands in sight of a country he will never enter — barred over a rock he was told to speak to and struck instead. In the middle of it he gives them the Shema, which means hear. *Hear, Israel: the LORD is our God. The LORD is one. You shall love the LORD your God with all your heart, with all your soul, and with all your might.* Jews have recited it twice a day ever since.",

        "He tells them to bind those words on their hands and write them on their doorposts. Observant Jewish homes still fix a small case to the doorframe for exactly that reason. He sets out blessings and curses, and puts the choice plainly — life and death, and *therefore choose life*. Then he climbs Nebo, looks across the Jordan at everything promised to Abraham, and dies there. Nobody knows where he is buried. Five books that began in a garden end with an unmarked grave on the wrong side of a river.",
      ],
      verseId: 'deu-6-5',
      takeaway: 'Unbelief cost a generation the land; the command was always to love, not merely obey.',
      questions: [
        {
          id: 'sq-wilderness-1',
          question: 'Why did Israel spend forty years in the wilderness?',
          options: [
            'Moses lost the way',
            'The route was blocked by Egyptian garrisons',
            'They refused to enter the land when they first reached its border',
            'They were searching for Mount Sinai',
          ],
          answer: 2,
          explanation:
            'Numbers 13–14. Ten of the twelve scouts reported that the land could not be taken, and the people believed them over God. The sentence was one year for each day the scouts had spent inside.',
        },
        {
          id: 'sq-wilderness-2',
          question: 'How long should the journey from the mountain to the promised land have taken?',
          options: ['Eleven days', 'Three months', 'A year', 'Seven years'],
          answer: 0,
          explanation:
            'Deuteronomy 1:2 states it flatly, as a route description, and lets the reader do the arithmetic against the forty years that actually followed.',
        },
        {
          id: 'sq-wilderness-3',
          question: 'Which figure from these books has turned up by name in an inscription found outside the Bible?',
          options: ['Moses', 'Korah', 'Aaron', 'Balaam'],
          answer: 3,
          explanation:
            'The Deir Alla texts, excavated in Jordan in 1967, open with the words of Balaam son of Beor, a seer of the gods. The inscription is not a copy of Numbers — it is an independent memory of the same figure.',
        },
        {
          id: 'sq-wilderness-4',
          question: 'Which command from Deuteronomy did Jesus later name as the greatest?',
          options: [
            'You shall not make a carved image',
            'Love the LORD your God with all your heart, soul and might',
            'Remember the sabbath day, to keep it holy',
            'Honour your father and your mother',
          ],
          answer: 1,
          explanation:
            'Deuteronomy 6:5, the second half of the Shema. Asked to rank the law, Jesus quotes the sentence Israel had been reciting twice a day since Moses gave it on the plains of Moab.',
        },
      ],
    },
  ],
};
